import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useSparks } from '../contexts/SparksContext';
import { get, post, put, del } from '../utils/api'; // Import put function
import { getLiveQuestionId, getLessonLiveQuestionId } from '../utils/liveQuestionId';
import { useAuth } from '../contexts/AuthContext';
import { confirmAlert } from 'react-confirm-alert';
import { Button } from '@/components/shadcn/button';
import { useNavigate } from 'react-router-dom';
import HomePageV2 from './HomePageV2';
import QuizPage from '../views/Quiz/Quiz';
import { useDaily } from '@daily-co/daily-react';
import { finalizeForced, getRedQueue } from '../api/dashboardApi';
import StudentChat from './Chat/StudentChat';
import LessonRecap from './Sparks/LessonRecap';

import 'react-confirm-alert/src/react-confirm-alert.css';

const REMEDIATION_DUPLICATE_GUARD_WINDOW_MS = 10 * 60 * 1000;

function LearningSession({ studentSessionid }) {

    const [currentQuizSubmission, setCurrentQuizSubmission] = useState(null);
    const [currentView, setCurrentView] = useState(null);

    const [sessionStarted, setSessionStarted] = useState(false);
    const [currentTopic, setCurrentTopic] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const [quizJustFinished, setQuizJustFinished] = useState(false);
    // Assessment finished: the server has already allocated the next lesson — the
    // interstitial congratulates and its button starts that lesson.
    const [assessmentJustFinished, setAssessmentJustFinished] = useState(false);
    const [lessonJustFinished, setLessonJustFinished] = useState(false);

    const [lessonId, setLessonId] = useState(null);
    const [topicId, setTopicId] = useState(null);

    // Red gate enforcement state
    const [isGateActive, setIsGateActive] = useState(false);
    const [forcedRedLessonId, setForcedRedLessonId] = useState(null);

    const navigate = useNavigate();

    const { user, logout, loggedInChild } = useAuth();
    const sparks = useSparks();
    // Latest balance for the learning-state-update payload without re-creating the memoised callbacks.
    const sparksBalanceRef = useRef(0);
    useEffect(() => { sparksBalanceRef.current = sparks.balance; }, [sparks.balance]);

    const callObject = useDaily();

    const initializationRef = useRef(false);

    // Tracks the ID of the question the student is currently on so that every
    // student-input-update message carries a questionId. TutorSingleQuestionPage
    // uses this to discard stale input payloads that arrive for the previous
    // question after the tutor has already moved to the next one.
    const currentQuestionIdRef = useRef(null);

    // Stable refs for callObject and sessionStarted so that handleLessonStateChange
    // and handleQuizStateChange never need to be recreated when these values change.
    // Previously both callbacks listed callObject and sessionStarted as useCallback deps,
    // meaning every session-start or Daily.co initialisation produced new function
    // references — which caused HomePageV2 and QuizPage to re-render, their onStateChange
    // useEffects to fire, and another learning-state-update broadcast to be sent,
    // which in turn caused QuestionPage's input-reset effect to fire mid-keystroke.
    const callObjectRef = useRef(callObject);
    const sessionStartedRef = useRef(sessionStarted);

    useEffect(() => { callObjectRef.current = callObject; }, [callObject]);
    useEffect(() => { sessionStartedRef.current = sessionStarted; }, [sessionStarted]);

    useEffect(() => {
        if (initializationRef.current) return;

        if (!loggedInChild) {
            confirmAlert({
                title: 'No Child Logged In',
                message: 'Please log in as a child to access the learning session.',
                buttons: [
                    {
                        label: 'Go to Children Dashboard',
                        onClick: () => navigate('/children-dashboard')
                    }
                ],
                closeOnEscape: false,
                closeOnClickOutside: false
            });
            return;
        }

        fetchCurrentTopic();

        initializationRef.current = true;
    }, [/*loggedInChild*/]);

    // Normal classroom flow — no forced-red branching
    const handleStartQuiz = async (lessonData, childTopicData, userData, loggedInChildData) => {
        try {
            const quizTrack = isGateActive ? 'remediation' : 'classroom';

            const stageResponse = await put(`/childTopics/${loggedInChildData._id}/${childTopicData.topicId}/learningStage`,
                { learningStage: 'quiz', track: quizTrack },
                true
            );
            sparks.pushFromResponse(stageResponse); // "Lesson done! +100" (Sparks §2.2)

            const response = await post('/quiz/quiz-submissions', {
                lessonId: lessonData._id,
                userId: userData._id,
                childId: loggedInChildData._id,
                topicId: childTopicData.topicId,
                QuizStatus: 'Pending',
                isCoreSkill: false,
                track: quizTrack,
                isForcedRed: isGateActive,
            }, true);

            setCurrentQuizSubmission(response._id);
            setLessonId(lessonData._id);
            setTopicId(childTopicData.topicId);
            setSessionStarted(false);
            setLessonJustFinished(true);
            setCurrentView('quiz');
        } catch (error) {
            console.error('Error starting quiz:', error);
        }
    };

    const finalizeRecentlyCompletedRemediationIfNeeded = async ({
        childId,
        topicId,
        lessonId,
        sessionBoundary,
    }) => {
        if (!childId || !topicId || !lessonId) return false;

        try {
            const completedRemediation = await get(
                `/quiz/quiz-submissions/search?childId=${childId}&topicId=${topicId}&lessonId=${lessonId}&QuizStatus=Complete&isHomework=false&track=remediation&isForcedRed=true`,
                true,
            );

            if (!Array.isArray(completedRemediation) || completedRemediation.length === 0) {
                return false;
            }

            const latestCompleted = completedRemediation
                .filter((submission) => submission?._id && submission?.completionDate)
                .sort(
                    (a, b) =>
                        new Date(b.completionDate).getTime() -
                        new Date(a.completionDate).getTime(),
                )[0];

            if (!latestCompleted) return false;

            const completedAtMs = new Date(latestCompleted.completionDate).getTime();
            if (Number.isNaN(completedAtMs)) return false;

            if (sessionBoundary) {
                const sessionBoundaryMs = new Date(sessionBoundary).getTime();
                if (!Number.isNaN(sessionBoundaryMs) && completedAtMs < sessionBoundaryMs) {
                    return false;
                }
            }

            const completedRecently =
                Date.now() - completedAtMs <= REMEDIATION_DUPLICATE_GUARD_WINDOW_MS;
            if (!completedRecently) return false;

            await finalizeForced(childId, latestCompleted._id);
            setIsGateActive(false);
            setForcedRedLessonId(null);
            setQuizJustFinished(true);
            setSessionStarted(false);
            return true;
        } catch (error) {
            console.error('Error while healing recently completed remediation:', error);
            return false;
        }
    };


    const fetchCurrentTopic = async () => {
        try {

            // when child is progressing through lessons, do we need to filter to lessons for which lessonVisible = true?
            // First get current classroom topic (separate from home track)
            const currentTopicResponse = await get(`/children/${loggedInChild._id}/current-classroom-topic`, true);

            // Then get childTopic if we have a classroomCurrentTopicId
            let childTopicResponse = null;
            if (currentTopicResponse.classroomCurrentTopicId) {
                childTopicResponse = await get(
                    `/childTopics/${loggedInChild._id}/${currentTopicResponse.classroomCurrentTopicId._id}?track=classroom`,
                    true
                );
            }

            // If either is missing, we need to set up default topic
            if (!currentTopicResponse.classroomCurrentTopicId || !childTopicResponse) {
                // Get the "View Lesson" topic
                const viewLessonTopic = await get('/topics/search?topic=View Lesson', true);

                if (!viewLessonTopic) {
                    throw new Error('View Lesson topic not found');
                }

                // Find appropriate lesson based on year group
                let firstLesson = null;
                for (const lessonObj of viewLessonTopic.lessons) {
                    const lessonYearGroups = lessonObj.lessonId.yearGroupLevels;
                    const matchesYearGroup = Array.isArray(lessonYearGroups) && lessonYearGroups.length > 0
                        ? lessonYearGroups.includes(loggedInChild.yearGroup)
                        : lessonObj.lessonId.yearGroupLevel === loggedInChild.yearGroup;
                    if (matchesYearGroup) {
                        firstLesson = lessonObj.lessonId;
                        break;
                    }
                }

                // If no lesson matches year group, use first lesson
                if (!firstLesson && viewLessonTopic.lessons.length > 0) {
                    firstLesson = viewLessonTopic.lessons[0].lessonId;
                }

                if (!firstLesson) {
                    throw new Error('No lessons found in View Lesson topic');
                }

                // Update child's classroomCurrentTopicId if needed
                if (!currentTopicResponse.classroomCurrentTopicId) {
                    await put(`/children/${loggedInChild._id}/current-classroom-topic`, {
                        topicId: viewLessonTopic._id
                    }, true);
                }

                // Create childTopic if needed
                if (!childTopicResponse) {
                    await post('/childTopics/start-learning', {
                        childId: loggedInChild._id,
                        topicId: viewLessonTopic._id,
                        lessonId: firstLesson._id,
                        track: 'classroom',
                    }, true);
                }

                // Fetch updated topic
                const updatedTopic = await get(`/children/${loggedInChild._id}/current-classroom-topic`, true);
                setCurrentTopic(updatedTopic.classroomCurrentTopicId);
            } else {
                setCurrentTopic(currentTopicResponse.classroomCurrentTopicId);
            }

            // Check if child was mid-remediation before the page loaded — resume if so
            const remediationTopicResponse = await get(`/children/${loggedInChild._id}/current-remediation-topic`, true);
            if (remediationTopicResponse?.remediationCurrentTopicId) {
                const topicId = String(remediationTopicResponse.remediationCurrentTopicId._id);
                const remediationChildTopic = await get(`/childTopics/${loggedInChild._id}/${topicId}?track=remediation`, true);
                if (remediationChildTopic?.currentLesson) {
                    const lessonId = String(remediationChildTopic.currentLesson._id);
                    setIsGateActive(true);
                    setForcedRedLessonId(lessonId);
                    if (remediationChildTopic.learningStage === 'quiz') {
                        const quizSubs = await get(`/quiz/quiz-submissions/search?childId=${loggedInChild._id}&topicId=${topicId}&lessonId=${lessonId}&QuizStatus=Pending&isHomework=false&track=remediation&isForcedRed=true`, true);
                        if (quizSubs?.length > 0) {
                            setCurrentQuizSubmission(quizSubs[0]._id);
                            setLessonId(lessonId);
                            setTopicId(topicId);
                        } else {
                            const healed = await finalizeRecentlyCompletedRemediationIfNeeded({
                                childId: loggedInChild._id,
                                topicId,
                                lessonId,
                                sessionBoundary: remediationChildTopic.updatedAt ?? remediationChildTopic.createdAt,
                            });
                            if (healed) {
                                setCurrentView(null);
                                setLoading(false);
                                return;
                            }
                            try {
                                const createRes = await post('/quiz/quiz-submissions', {
                                    lessonId,
                                    userId: user._id,
                                    childId: loggedInChild._id,
                                    topicId,
                                    QuizStatus: 'Pending',
                                    isCoreSkill: false,
                                    track: 'remediation',
                                    isForcedRed: true,
                                }, true);
                                setCurrentQuizSubmission(createRes._id);
                                setLessonId(lessonId);
                                setTopicId(topicId);
                            } catch (err) {
                                console.error('Error creating missing remediation quiz submission:', err);
                            }
                        }
                        setCurrentView('quiz');
                    } else {
                        setCurrentView('lesson');
                    }
                    setSessionStarted(true);
                }
            }

            setLoading(false);
        } catch (err) {
            console.error('Error in fetchCurrentTopic:', err);
            setError('Failed to fetch current topic');
            setLoading(false);
        }
    };

    const handleContinue = async () => {

        setLessonJustFinished(false);
        setQuizJustFinished(false);
        setAssessmentJustFinished(false);

        // Check gate whenever Continue is NOT a lesson→quiz transition.
        // This handles: (a) quiz just finished, and (b) new session where the
        // counter already reached the threshold in a previous session but the
        // gate never fired because the queue was empty at that time.
        if (!lessonJustFinished) {
            try {
                const childData = await get(`/children/${loggedInChild._id}`, true);
                if (childData?.gateActive) {
                    const redQueueData = await getRedQueue(loggedInChild._id);
                    // Server picks the head (first lesson not skipped by an admin).
                    const headLessonId = redQueueData?.headLessonId ?? redQueueData?.queue?.[0]?.lessonId ?? null;
                    if (headLessonId) {
                        // Create remediation childTopic and set pointer on child
                        await post('/childTopics/start-learning', {
                            childId: loggedInChild._id,
                            topicId: currentTopic._id,
                            lessonId: headLessonId,
                            track: 'remediation',
                        }, true);
                        await put(`/children/${loggedInChild._id}/current-remediation-topic`,
                            { topicId: currentTopic._id }, true);
                        setIsGateActive(true);
                        setForcedRedLessonId(headLessonId);
                        setCurrentView('lesson');
                        setSessionStarted(true);
                        return;
                    }
                }
            } catch (err) {
                console.error('Error checking red gate:', err);
                // Continue with normal flow if gate check fails
            }
        }

        const childTopicTrack = isGateActive ? 'remediation' : 'classroom';
        const response = await get(`/childTopics/${loggedInChild._id}/${currentTopic._id}?track=${childTopicTrack}`, true);
        const { currentLesson, learningStage } = response;

        if (learningStage === 'quiz') {
            const forcedRedParam = isGateActive ? 'true' : 'false';
            const quizSubmissions = await get(
                `/quiz/quiz-submissions/search?childId=${loggedInChild._id}&topicId=${currentTopic._id}&lessonId=${currentLesson._id}&QuizStatus=Pending&isHomework=false&track=${childTopicTrack}&isForcedRed=${forcedRedParam}`,
                true
            );

            if (quizSubmissions && quizSubmissions.length > 0) {
                const quizSubmissionId = quizSubmissions[0]._id;
                setCurrentQuizSubmission(quizSubmissionId);
                setLessonId(currentLesson._id);
                setTopicId(currentTopic._id);
            } else {
                if (isGateActive) {
                    const healed = await finalizeRecentlyCompletedRemediationIfNeeded({
                        childId: loggedInChild._id,
                        topicId: currentTopic._id,
                        lessonId: currentLesson._id,
                        sessionBoundary: response.updatedAt ?? response.createdAt,
                    });
                    if (healed) {
                        return;
                    }
                }
                console.log('No pending quiz submission found, creating a new one');
                try {
                    const createRes = await post('/quiz/quiz-submissions', {
                        lessonId: currentLesson._id,
                        userId: user._id,
                        childId: loggedInChild._id,
                        topicId: currentTopic._id,
                        QuizStatus: 'Pending',
                        isCoreSkill: false,
                        track: childTopicTrack,
                        isForcedRed: isGateActive,
                    }, true);

                    setCurrentQuizSubmission(createRes._id);
                    setLessonId(currentLesson._id);
                    setTopicId(currentTopic._id);
                } catch (error) {
                    console.error('Error creating new quiz submission:', error);
                }
            }

        }

        setCurrentView(learningStage);
        setSessionStarted(true);
    }


    const handleQuizCompleted = async (completedQuizSubmission, forceGateNow) => {
        try {
            // Remediation quiz complete — clean up and return to classroom flow
            const isForcedRemediationSubmission =
                completedQuizSubmission?.track === 'remediation' &&
                completedQuizSubmission?.isForcedRed === true;

            if (isGateActive || isForcedRemediationSubmission) {
                // The server now releases the gate authoritatively when the forced
                // remediation quiz reaches Complete. This call is only a best-effort
                // backup (idempotent server-side), so never let its failure trap the
                // UI in the gate — always clear local gate state and let the next
                // /children fetch reflect server truth.
                try {
                    await finalizeForced(loggedInChild._id, completedQuizSubmission._id);
                } catch (err) {
                    console.error('Backup finalizeForced failed; server reset is authoritative:', err);
                }
                setIsGateActive(false);
                setForcedRedLessonId(null);
                setQuizJustFinished(true);
                setSessionStarted(false);
                return;
            }

            // First, get the full topic document to access the ordered lessons
            const topicResponse = await get(`/topics/${currentTopic._id}`);

            // Get the current childTopic to find current lesson
            const childTopicResponse = await get(`/childTopics/${loggedInChild._id}/${currentTopic._id}?track=classroom`, true);
            const currentLessonId = childTopicResponse.currentLesson._id;

            // Assessment completions are advanced SERVER-side: the auto-allocator has
            // already pointed the child at the right skill lesson during the completion
            // PATCH (or its reconciliation sweep will). Clearing the pointer / deleting
            // the ChildTopic here would clobber that allocation — and a cleared pointer
            // also makes the allocator's retry guard skip the child. Instead, refresh the
            // topic pointer from the server so the "well done" interstitial's button
            // starts the allocated lesson, never the (stale) assessment again.
            if (childTopicResponse.currentLesson.isAssessment) {
                try {
                    const fresh = await get(`/children/${loggedInChild._id}/current-classroom-topic`, true);
                    setCurrentTopic(fresh?.classroomCurrentTopicId ?? null);
                } catch (err) {
                    console.error('Error refreshing allocated topic after assessment:', err);
                }
                setAssessmentJustFinished(true);
                setSessionStarted(false);
                return;
            }

            // Find current lesson's position in the ordered lessons array
            const currentLessonIndex = topicResponse.lessons.findIndex(
                lesson => lesson.lessonId._id === currentLessonId
            );

            // Check if there's a next lesson
            if (currentLessonIndex < topicResponse.lessons.length - 1) {
                // Get the next lesson
                const nextLesson = topicResponse.lessons[currentLessonIndex + 1].lessonId;

                // Update childTopic with next lesson
                await put(`/childTopics/${loggedInChild._id}/${currentTopic._id}`, {
                    currentLesson: nextLesson._id,
                    currentLessonStep: 0,
                    learningStage: 'lesson',
                    track: 'classroom',
                }, true);

                setQuizJustFinished(true);
                setSessionStarted(false);
            } else {
                // This was the last lesson in the topic
                // Clear the classroom current topic from the child
                await put(`/children/${loggedInChild._id}/current-classroom-topic`, { topicId: null }, true);

                // Delete the childTopic entry
                await del(`/childTopics/${loggedInChild._id}/${currentTopic._id}?track=classroom`, true);

                // Handle completion of entire topic
                setQuizJustFinished(true);
                setSessionStarted(false);
            }
        } catch (error) {
            console.error('Error handling quiz completion:', error);
        }
    };

    //-------------------------------------------------------------------------------
    // Functions to send Daily.co messages from student to teacher
    //-------------------------------------------------------------------------------

    // Empty dep arrays keep these callbacks stable for the lifetime of the component.
    // callObject and sessionStarted are read from refs (kept in sync above) so the
    // function identity never changes — HomePageV2 and QuizPage will not re-render
    // just because the Daily.co call initialised or the session started.
    // studentSessionid is the only true external value needed in the payload; it is
    // a prop and should be stable across a session, so it is safe to keep as a dep.
    const handleLessonStateChange = useCallback((lessonState) => {
        // Keep the ref in sync so handleStudentInput attaches the in-lesson
        // question id (or null on non-question steps). Without this, a leftover
        // quiz id makes TutorSingleQuestionPage discard lesson blank-box input.
        currentQuestionIdRef.current = getLessonLiveQuestionId(lessonState);

        const newState = {
            // Same as quiz: tutor tile shows the child's Sparks with no API traffic (§2.6).
            sparksBalance: sparksBalanceRef.current,
            latestLesson: lessonState.latestLesson,
            currentView: 'lesson',
            currentStep: lessonState.currentLessonStep,
            totalSteps: lessonState.totalSteps,
            isQuizMode: false,
            currentQuestionIndex: null,
            totalQuestions: null,
        };

        if (callObjectRef.current && sessionStartedRef.current) {
            callObjectRef.current.sendAppMessage({
                type: 'learning-state-update',
                data: {
                    studentSessionid: studentSessionid,
                    state: newState
                }
            });
        }
    }, [studentSessionid]);

    const handleQuizStateChange = useCallback((quizState) => {
        // Keep the ref in sync so handleStudentInput can attach the correct
        // questionId to every student-input-update message.
        currentQuestionIdRef.current = getLiveQuestionId(quizState);

        const newState = {
            // Carried browser-to-browser so the tutor tile can show the child's Sparks with no API traffic (§2.6).
            sparksBalance: sparksBalanceRef.current,
            quizSubmission: quizState.quizSubmission,
            currentView: 'quiz',
            currentStep: null,
            totalSteps: null,
            isQuizMode: true,
            currentQuestionIndex: quizState.currentQuestionIndex,
            currentQuestionId: quizState.currentQuestionId,
            totalQuestions: quizState.totalQuestions,
            sessionStarted: quizState.quizStarted,
            quizStatus: quizState.quizStatus,
            showingExplanation: quizState.showingExplanation,
            currentExplanationQuestion: quizState.currentExplanationQuestion
        };

        if (callObjectRef.current && sessionStartedRef.current) {
            callObjectRef.current.sendAppMessage({
                type: 'learning-state-update',
                data: {
                    studentSessionid: studentSessionid,
                    state: newState
                }
            });
        }
    }, [studentSessionid]);

    // Assessment countdown sync: a tiny dedicated message (instead of a full
    // learning-state-update) so the tutor's timer pill can update without
    // replacing studentLearningStates — the mirrored lesson content on the
    // tutor's tiles is not re-rendered by these syncs.
    const handleTimerSync = useCallback((remainingSeconds) => {
        if (callObjectRef.current && sessionStartedRef.current) {
            callObjectRef.current.sendAppMessage({
                type: 'assessment-timer-sync',
                data: {
                    studentSessionid: studentSessionid,
                    remainingSeconds: remainingSeconds
                }
            });
        }
    }, [studentSessionid]);

    const handleStudentInput = useCallback((inputData) => {
        if (callObjectRef.current) {
            callObjectRef.current.sendAppMessage({
                type: 'student-input-update',
                data: {
                    studentSessionid: studentSessionid,
                    questionType: inputData.questionType,
                    input: inputData.value,
                    isSubmitted: inputData.isSubmitted,
                    isCorrect: inputData.isCorrect,
                    // Included so TutorSingleQuestionPage can discard stale input
                    // that belongs to the previous question (Phase 6 fix).
                    questionId: currentQuestionIdRef.current
                }
            });
        }
    }, [studentSessionid]);

    //-------------------------------------------------------------------------------


    // If no child is logged in, don't render the main content
    if (!loggedInChild) {
        return null; // The alert will show and redirect
    }

    return (
        <div style={{ height: '100%' }}>

            {!currentTopic && (
                <div className="h-screen flex items-center justify-center">
                    <p>PLEASE ASK YOUR TUTOR TO SET UP A LESSON FOR YOU</p>
                </div>
            )}

            {currentTopic && !sessionStarted && (
                (quizJustFinished || assessmentJustFinished) ? (
                    // No shop button: it would take the child out of the live lesson.
                    <div className="min-h-screen flex flex-col items-center justify-center">
                        <LessonRecap
                            continueLabel={assessmentJustFinished ? 'Start your next lesson' : 'Continue Learning'}
                            onContinue={handleContinue}
                        >
                            {assessmentJustFinished ? 'Well done on completing your assessment! 🎉' : 'Well done on finishing the practice questions!'}
                        </LessonRecap>
                    </div>
                ) : (
                <div className="h-screen flex flex-col items-center justify-center">
                    {lessonJustFinished && (
                        <p>
                            Well done on finishing the lesson! Click the button to continue to the practice questions!
                        </p>
                    )}

                    <Button
                        onClick={handleContinue}
                        className="bg-indigo-600 hover:bg-indigo-700 text-white font-bold py-2 px-4 rounded focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                    >
                        Continue Learning
                    </Button>
                </div>
                )
            )}

            {/* Lesson view — handles both classroom and remediation */}
            {currentTopic && sessionStarted && currentView === 'lesson' && (
                <HomePageV2
                    onQuizStart={handleStartQuiz}
                    preventNavigation={true}
                    onStateChange={handleLessonStateChange}
                    onInputChange={handleStudentInput}
                    resolvedTrack={isGateActive ? 'remediation' : 'classroom'}
                />
            )}

            {/* Quiz view — handles both classroom and remediation */}
            {currentTopic && sessionStarted && currentView === 'quiz' && (
                <QuizPage
                    quizSubmissionId={currentQuizSubmission}
                    lessonId={lessonId}
                    topicId={topicId}
                    onQuizComplete={handleQuizCompleted}
                    preventNavigation={true}
                    onStateChange={handleQuizStateChange}
                    onInputChange={handleStudentInput}
                    onTimerSync={handleTimerSync}
                />
            )}

            {/* Fallback: prevents a blank screen during view transitions */}
            {currentTopic && sessionStarted && currentView !== 'lesson' && currentView !== 'quiz' && (
                <div className="h-screen flex items-center justify-center">
                    <p>Loading...</p>
                </div>
            )}

            {/* StudentChat subscribes to context internally — no chat state in LearningSession */}
            <StudentChat />
        </div>
    );
}

export default LearningSession;
